"use client";

import type { ChangeEvent } from "react";

export type BuilderDimensionConfig = {
  defaultValue: number;
  max: number;
  min: number;
  step: number;
  unit: "" | "%" | "ms" | "px";
};

export function builderDimensionNumber(value: unknown): number | null {
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : null;
  }

  if (typeof value !== "string" || value.trim() === "") {
    return null;
  }

  const match = value.trim().match(
    /^(-?\d+(?:\.\d+)?)(?:px|ms|%|rem|em|vw|vh)?$/iu,
  );
  const parsed = match ? Number.parseFloat(match[1]) : Number.NaN;

  return Number.isFinite(parsed) ? parsed : null;
}

export function builderFormatDimensionValue(
  value: number,
  config: BuilderDimensionConfig,
): string {
  const stepped = Math.round(value / config.step) * config.step;
  const precision = config.step < 1 ? 2 : 0;
  const formatted = stepped
    .toFixed(precision)
    .replace(/\.0+$/u, "")
    .replace(/(\.\d*[1-9])0+$/u, "$1");

  return `${formatted}${config.unit}`;
}

export function builderNormalizeDimensionValue(
  value: string,
  config: BuilderDimensionConfig,
): string {
  const trimmed = value.trim();

  if (trimmed === "") {
    return "";
  }

  if (/^-?\d+(?:\.\d+)?$/u.test(trimmed)) {
    return `${trimmed}${config.unit}`;
  }

  return trimmed;
}

function inputValue(value: unknown): string | number {
  return typeof value === "string" || typeof value === "number"
    ? value
    : "";
}

export function BuilderDimensionField({
  adjustLabel,
  config,
  label,
  onCommit,
  onPreview,
  onReset,
  resetLabel,
  value,
}: {
  adjustLabel: string;
  config: BuilderDimensionConfig;
  label: string;
  onCommit: (value: string) => void;
  onPreview: (value: string) => void;
  onReset: () => void;
  resetLabel: string;
  value: unknown;
}) {
  const rawValue = inputValue(value);
  const rawText = String(rawValue).trim();
  const configuredUnitMatch = config.unit
    ? rawText.match(new RegExp(
        `^(-?\\d+(?:\\.\\d+)?)${config.unit}$`,
        "iu",
      ))
    : rawText.match(/^(-?\d+(?:\.\d+)?)$/u);
  const usesConfiguredUnit = rawText === ""
    || configuredUnitMatch !== null
    || typeof rawValue === "number"
    || /^-?\d+(?:\.\d+)?$/u.test(rawText);
  const valueText = configuredUnitMatch?.[1] ?? rawText;
  const numericValue = builderDimensionNumber(value);
  const sliderValue = Math.max(
    config.min,
    Math.min(config.max, numericValue ?? config.defaultValue),
  );
  const normalize = (event: ChangeEvent<HTMLInputElement>) => (
    builderNormalizeDimensionValue(event.target.value, config)
  );

  return (
    <div
      className="grid grid-cols-[96px_minmax(0,1fr)] items-center gap-2 py-1.5 text-xs"
      data-builder-dimension-field="true"
    >
      <span className="min-w-0 text-text">{label}</span>
      <div className="min-w-0 space-y-1">
        <div className="flex items-center gap-2">
          <input
            type="range"
            aria-label={adjustLabel}
            className="min-w-0 flex-1 accent-primary"
            max={config.max}
            min={config.min}
            step={config.step}
            value={sliderValue}
            onChange={(event) => {
              onCommit(builderFormatDimensionValue(
                Number(event.target.value),
                config,
              ));
            }}
          />
          <div className="flex h-8 w-20 shrink-0 items-center overflow-hidden rounded border border-border bg-surface">
            <input
              type="text"
              aria-label={label}
              className="min-w-0 flex-1 bg-transparent px-2 text-right text-xs text-text outline-none"
              placeholder={String(config.defaultValue)}
              value={valueText}
              onBlur={(event) => onCommit(normalize(event))}
              onChange={(event) => onPreview(normalize(event))}
            />
            {config.unit && usesConfiguredUnit ? (
              <span className="pr-2 text-[11px] font-semibold text-text-secondary">
                {config.unit}
              </span>
            ) : null}
          </div>
        </div>
        <div className="flex items-center justify-between gap-2 text-[11px] text-text-secondary">
          <span>{config.min}{config.unit}</span>
          <button
            type="button"
            className="rounded bg-surface px-2 py-1 text-[10px] font-semibold text-text transition-colors hover:bg-surface"
            onClick={onReset}
          >
            {resetLabel}
          </button>
          <span>{config.max}{config.unit}</span>
        </div>
      </div>
    </div>
  );
}
